import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { Droppable, Draggable } from '@hello-pangea/dnd';

const RecipeVault = ({ isOpen, onClose, recipes, selectedRecipeForVault }) => {
  const { user } = useAuth();
  const [showManualForm, setShowManualForm] = useState(false);
  const [viewingRecipe, setViewingRecipe] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editBuffer, setEditBuffer] = useState(null); 
  
  useEffect(() => {
    if (selectedRecipeForVault) {
      setViewingRecipe(selectedRecipeForVault);
    }
  }, [selectedRecipeForVault]);
  
  // Form State
  const [manualRecipe, setManualRecipe] = useState({ 
    title: '', 
    rawText: '', 
    image: '',
    sourceUrl: '' 
  });

  // --- 1. THE SAVING LOGIC ---
  const handleSaveManual = async () => {
    if (!manualRecipe.title || !user) {
      alert("Please add at least a title!");
      return;
    }

    // Split at "Instructions" (case insensitive)
    const parts = manualRecipe.rawText.split(/Instructions/i);
    const ingredientSection = parts[0] || "";
    const instructionSection = parts[1] || "";

    // Clean up ingredients: remove checkboxes, trim, and filter out short/empty lines
    const ingredientArray = ingredientSection
      .split('\n')
      .map(line => line.replace(/[▢❑☐]/g, '').trim()) // Remove various checkbox styles
      .filter(line => line.length > 2); 

    try {
      await addDoc(collection(db, 'recipes'), {
        title: manualRecipe.title,
        sourceUrl: manualRecipe.sourceUrl,
        ingredients: ingredientArray,
        instructions: instructionSection.trim(),
        image: manualRecipe.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(manualRecipe.title)}&background=6366f1&color=fff`,
        userId: user.uid,
        createdAt: serverTimestamp()
      });

      // Reset
      setManualRecipe({ title: '', rawText: '', image: '', sourceUrl: '' });
      setShowManualForm(false);
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  const startEditing = () => {
    // Clone the current recipe into a buffer so we don't mess up the original until we hit Save
    setEditBuffer({
        ...viewingRecipe,
        ingredients: viewingRecipe.ingredients.join('\n'), 
        sourceUrl: viewingRecipe.sourceUrl || ''
    });
    setIsEditing(true);
    };

    const handleUpdateRecipe = async () => {
        try {
            const recipeRef = doc(db, 'recipes', viewingRecipe.id);
            const updatedData = {
            title: editBuffer.title,
            sourceUrl: editBuffer.sourceUrl, 
            instructions: editBuffer.instructions,
            ingredients: editBuffer.ingredients.split('\n').filter(line => line.trim() !== '')
            };
            await updateDoc(recipeRef, updatedData);
            setViewingRecipe({ ...viewingRecipe, ...updatedData });
            setIsEditing(false);
        } catch (err) { console.error(err); }
    };

return (
  <>
    <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 border-l border-gray-100 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
      
      {/* Header */}
      <div className="p-6 border-b flex justify-between items-center bg-gray-50/50">
        <div>
          <h2 className="text-xl font-black text-gray-800 tracking-tight">Recipe Vault</h2>
          <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">Your Private Repository</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">✕</button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Add New Recipe Section */}
        {!showManualForm ? (
          <button 
            onClick={() => setShowManualForm(true)}
            className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 font-bold text-sm hover:border-indigo-300 hover:text-indigo-500 transition-all mb-6"
          >
            + Add New Recipe
          </button>
        ) : (
          <div className="space-y-4 mb-8 bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100">
            <div className="flex justify-between items-center">
              <span className="text-xs font-black text-indigo-600 uppercase">New Recipe</span>
              <button onClick={() => setShowManualForm(false)} className="text-[10px] font-bold text-gray-400 underline">Cancel</button>
            </div>
            <input 
              className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none"
              placeholder="Recipe Title"
              value={manualRecipe.title}
              onChange={e => setManualRecipe({...manualRecipe, title: e.target.value})}
            />
            <textarea 
              className="w-full p-3 border border-gray-200 rounded-xl text-xs h-32 outline-none font-mono"
              placeholder="Paste Ingredients and Instructions..."
              value={manualRecipe.rawText}
              onChange={e => setManualRecipe({...manualRecipe, rawText: e.target.value})}
            />
            <input 
                className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none"
                placeholder="Recipe Link (URL)"
                value={manualRecipe.sourceUrl}
                onChange={e => setManualRecipe({...manualRecipe, sourceUrl: e.target.value})}
            />
            <button onClick={handleSaveManual} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold text-sm">Save to Repository</button>
          </div>
        )}

        {/* DRAGGABLE LIST */}
        <Droppable droppableId="recipe-vault" isDropDisabled={true}>
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="grid grid-cols-1 gap-4">
              {recipes.map((recipe, index) => (
                <Draggable key={recipe.id} draggableId={recipe.id} index={index}>
                  {(provided, snapshot) => (
                    <>
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={() => setViewingRecipe(recipe)} 
                        className={`group flex items-center p-3 gap-4 bg-white rounded-2xl border transition-all cursor-grab active:cursor-grabbing ${
                          snapshot.isDragging ? 'border-indigo-500 shadow-xl ring-2 ring-indigo-100 scale-105' : 'border-gray-100 shadow-sm hover:shadow-md'
                        }`}
                      >
                        <div className="flex-1 pr-4">
                          <h4 className="font-bold text-sm text-gray-800 line-clamp-1">{recipe.title}</h4>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter mt-1">{recipe.ingredients?.length || 0} Ingredients</p>
                        </div>
                        <div className="text-indigo-200 group-hover:text-indigo-500">→</div>
                      </div>
                      {snapshot.isDragging && <div className="h-16 w-full bg-gray-50 border border-dashed border-gray-200 rounded-2xl" />}
                    </>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>

    {/* QUICK VIEW & EDIT MODAL - FULL CONTENT RESTORED */}
    {viewingRecipe && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto shadow-2xl relative">
          
          <div className="sticky top-0 bg-white p-6 border-b flex justify-between items-center z-10">
            {isEditing ? (
              <input 
                className="text-xl font-black text-gray-800 border-b-2 border-indigo-500 outline-none w-full mr-4"
                value={editBuffer.title}
                onChange={e => setEditBuffer({...editBuffer, title: e.target.value})}
              />
            ) : (
              <h3 className="text-xl font-black text-gray-800">{viewingRecipe.title}</h3>
            )}
            <button onClick={() => { setViewingRecipe(null); setIsEditing(false); }} className="text-gray-400 text-2xl p-2">✕</button>
          </div>
          
          <div className="p-8 space-y-6">
            {isEditing ? (
              /* EDITING STATE */
              <div className="space-y-4">
                <div>
                    <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-2">Recipe Link</label>
                    <input 
                        className="w-full p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        value={editBuffer.sourceUrl}
                        onChange={e => setEditBuffer({...editBuffer, sourceUrl: e.target.value})}
                    />
                    </div>
                <div>
                  <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-2">Ingredients (one per line)</label>
                  <textarea 
                    className="w-full p-4 border rounded-2xl text-sm h-48 font-mono outline-none focus:ring-2 focus:ring-indigo-500"
                    value={editBuffer.ingredients}
                    onChange={e => setEditBuffer({...editBuffer, ingredients: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-2">Instructions</label>
                  <textarea 
                    className="w-full p-4 border rounded-2xl text-sm h-48 outline-none focus:ring-2 focus:ring-indigo-500"
                    value={editBuffer.instructions}
                    onChange={e => setEditBuffer({...editBuffer, instructions: e.target.value})}
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setIsEditing(false)} className="flex-1 py-4 bg-gray-100 rounded-2xl font-bold text-gray-500">Cancel</button>
                  <button onClick={handleUpdateRecipe} className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-100">Save Changes</button>
                </div>
              </div>
            ) : (
              /* VIEWING STATE */
              <>
                <div className="flex justify-end mt-[-1rem]">
                  <button onClick={startEditing} className="text-xs font-bold text-indigo-500 bg-indigo-50 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition">Edit Recipe</button>
                </div>
                
                {viewingRecipe.sourceUrl && (
                    <a 
                        href={viewingRecipe.sourceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs font-bold text-blue-500 hover:underline"
                    >
                        🔗 Source: {new URL(viewingRecipe.sourceUrl).hostname}
                    </a>
                )}

                <div>
                  <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-3">Ingredients</h4>
                  <ul className="space-y-3">
                    {viewingRecipe.ingredients?.map((ing, i) => {
                        // Logic: Is it a header? (Ends in colon OR is all caps and short)
                        const isHeader = ing.trim().endsWith(':') || (ing === ing.toUpperCase() && ing.length > 3);

                        return (
                        <li key={i} className={`text-sm ${isHeader ? 'font-black text-gray-800 pt-4 pb-1 border-b-2 border-indigo-100 mb-2' : 'text-gray-600 flex gap-3 border-b border-gray-50 pb-2'}`}>
                            {!isHeader && <span className="text-indigo-300 font-bold">•</span>}
                            {ing}
                        </li>
                        );
                    })}
                  </ul>
                </div>

                {viewingRecipe.instructions && (
                  <div>
                    <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-3">Instructions</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                      {viewingRecipe.instructions}
                    </p>
                  </div>
                )}
                
                <button 
                  className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition"
                  onClick={() => alert("Ready to plan this meal?")}
                >
                  Add to Meal Plan
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )}
  </>
);
};

export default RecipeVault;